/**
 * FCM Token Management
 * - registerFcmToken   → POST /api/v2/notifications/register-fcm-token   (authenticated)
 * - registerGuestToken → POST /api/v2/notifications/register-guest-fcm-token (no auth)
 *
 * On a dashboard (web, not mobile) we cannot get a real FCM token from Firebase
 * because service-workers require firebase SDK setup. Instead we register a stable
 * "web-dashboard" pseudo-token that the backend can choose to ignore or store.
 * The important thing is the API contract is fulfilled on login/logout.
 */

import { axiosPostRequest } from './Request';
import { endpoints } from './endpoints';

const DEVICE_ID_KEY = 'kotobekia_device_id';
const FCM_TOKEN_KEY = 'kotobekia_fcm_token';

/** Returns (creating if needed) a stable browser device ID */
function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-dashboard-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/** Stubbed web FCM token — real apps would call getToken() from firebase/messaging */
function getWebFcmToken(): string {
  let token = localStorage.getItem(FCM_TOKEN_KEY);
  if (!token) {
    // Generate a deterministic-looking pseudo token for the dashboard
    token = `web_${getDeviceId()}_${Date.now()}`;
    localStorage.setItem(FCM_TOKEN_KEY, token);
  }
  return token;
}

/**
 * Register the authenticated admin's FCM token.
 * Call this right after a successful login.
 */
export async function registerAuthenticatedFcmToken(): Promise<void> {
  try {
    await axiosPostRequest(endpoints.registerFcmToken, {
      token: getWebFcmToken(),
      deviceId: getDeviceId(),
      platform: 'web',
    });
  } catch {
    // Non-critical — swallow silently so it never breaks the login flow
  }
}

/**
 * Register a guest FCM token (no auth header).
 * Call this for unauthenticated landing pages if needed.
 */
export async function registerGuestFcmToken(): Promise<void> {
  try {
    await axiosPostRequest(endpoints.registerGuestFcmToken, {
      token: getWebFcmToken(),
      deviceId: getDeviceId(),
      platform: 'web',
    });
  } catch {
    // Non-critical
  }
}

/** Clear stored FCM token on logout so a fresh one is generated next session */
export function clearFcmToken(): void {
  localStorage.removeItem(FCM_TOKEN_KEY);
}
